import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { aiAllowedFor } from '../lib/appContext'
import { shipTodayAuto } from '../lib/shipTime'
import { supabase } from '../lib/supabase'
import { canSeeAllHistory } from './HistoryLink'
import { openGuide } from './GuideHelpPanel'
import { isDone, jobsFor, jobsRole, probeQuery, runProbe } from '../lib/tutorialCore'
import { tutorialStore } from '../lib/tutorialStore'
import '../styles/tutorial.css'

// The Getting started card (Dashboard, phone Today, Fleet page): 5-6 jobs for this login that tick themselves from real data. A tick
// stays once earned. "Show me" opens the page and its guide entry; "Do it with AI" sends the normal chat message, which ends in the
// normal approve card. Row count and size never change while it loads, so the page under it does not jump.
const REFRESH_MS = 60000
const WELL_DONE_MS = 7000

export default function GettingStarted() {
  const auth = useAuth()
  const { user, config, accountType, selectedVessel, can, hasFeature } = auth
  const store = tutorialStore(user && user.id)
  const [state, setState] = useState(() => (store ? store.get() : null))
  const [forced, setForced] = useState(false)    // reopened from the user menu: stays until closed, even with every job ticked
  const [cheer, setCheer] = useState(false)      // all done just now: the small "well done", then gone
  const aiAllowed = aiAllowedFor({ accountType, permissions: config?.permissions, can, hasFeature })
  const jobs = useMemo(() => jobsFor(auth, { aiAllowed, canSeeAllHistory }), [config?.id, config?.permissions, accountType, aiAllowed]) // eslint-disable-line
  const running = useRef(false)

  useEffect(() => { if (!store) return undefined; setState(store.get()); return store.subscribe(setState) }, [store])
  useEffect(() => {
    const h = () => { setForced(true); setCheer(false); if (store) store.set({ checklist_hidden: false }) }
    window.addEventListener('hc-checklist-reopen', h)
    return () => window.removeEventListener('hc-checklist-reopen', h)
  }, [store])

  const off = !store || !state || (accountType === 'company' && selectedVessel) || (!forced && state.checklist_hidden)
  const allDone = !!state && jobs.length > 0 && jobs.every(j => isDone(j, state.marks))
  const closed = !!state && state.checklist_done && !forced && !cheer

  // read the jobs still open; what is found is kept for good
  useEffect(() => {
    if (off || closed || allDone) return undefined
    let live = true
    const check = async () => {
      if (running.current || document.hidden) return
      running.current = true
      try {
        await store.mark('started')
        const m = store.get().marks
        const ctx = { uid: user.id, vesselId: config?.id, name: config?.crewName || '', since: m.started, today: shipTodayAuto(), role: jobsRole(auth) }
        const open = jobs.filter(j => j.probe && !isDone(j, m))
        const found = await Promise.all(open.map(j => runProbe(supabase, probeQuery(j.probe, ctx)).catch(() => false)))
        if (live) await store.mark(open.filter((j, i) => found[i]).map(j => j.id))
      } finally { running.current = false }
    }
    check()
    const id = setInterval(check, REFRESH_MS)
    return () => { live = false; clearInterval(id) }
  }, [off, closed, allDone, jobs, config?.id]) // eslint-disable-line

  // the last tick: say well done once, then the card is gone for good (the user menu brings it back)
  useEffect(() => {
    if (off || !allDone || state.checklist_done || forced) return undefined
    setCheer(true)
    store.set({ checklist_done: true })
    return undefined
  }, [off, allDone]) // eslint-disable-line
  useEffect(() => { if (!cheer) return undefined; const t = setTimeout(() => setCheer(false), WELL_DONE_MS); return () => clearTimeout(t) }, [cheer])

  if (off || !jobs.length) return null
  if (cheer && !forced) return <div className="hc-gs-done" role="status"><span aria-hidden="true">{'✓'}</span> Well done. You have finished Getting started.</div>
  if (closed) return null

  const count = jobs.filter(j => isDone(j, state.marks)).length
  const close = () => { setForced(false); setCheer(false); store.set({ checklist_hidden: true }) }
  const show = j => {
    if (j.page) window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: j.page } }))
    if (typeof j.fleet === 'string') window.dispatchEvent(new CustomEvent('hc-fleet-tab', { detail: { tab: j.fleet } }))
    openGuide({ id: j.guide })
  }
  const withAi = j => window.dispatchEvent(new CustomEvent('hc-assistant-open', { detail: { text: j.ai } }))
  return (
    <section className="hc-gs" aria-labelledby="hc-gs-h">
      <div className="hc-gs-head">
        <h2 id="hc-gs-h">Getting started</h2>
        <span className="hc-gs-count">{count} of {jobs.length}</span>
        <button type="button" className="hc-gs-x" onClick={close} aria-label="Hide Getting started" title="Hide. Your name in the menu brings it back.">{'✕'}</button>
      </div>
      <div className="hc-gs-bar" aria-hidden="true"><span style={{ width: (count / jobs.length) * 100 + '%' }} /></div>
      {allDone && <div className="hc-gs-all" role="status">Well done. Every job is done.</div>}
      <ul className="hc-gs-list">
        {jobs.map(j => {
          const done = isDone(j, state.marks)
          return (
            <li key={j.id} data-job={j.id} className={done ? 'done' : ''}>
              <span className="hc-gs-tick" role="img" aria-label={done ? 'Done' : 'Not done yet'}>{done ? '✓' : ''}</span>
              <span className="hc-gs-label">{j.label}</span>
              <span className="hc-gs-acts">
                <button type="button" onClick={() => show(j)}>Show me</button>
                {j.ai && !done && <button type="button" className="ai" onClick={() => withAi(j)}>Do it with AI</button>}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
